import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Plus, Search, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import CreatePostModal from '@/components/CreatePostModal'
import { useAuth } from '@/contexts/auth'
import DatePicker, { DatePickerMode } from '@/components/DatePicker'
import { DateRange } from 'react-day-picker'

interface TimelineHeaderProps {
  activeTab: string
  onSearch?: (_searchValue: string) => void
  searchValue?: string
  selectedDate?: DateRange
  onDateChange?: (_date: DateRange | undefined) => void
}

export function TimelineHeader({ 
  activeTab, 
  onSearch, 
  searchValue = '',
  selectedDate,
  onDateChange
}: TimelineHeaderProps) {
  const { user } = useAuth()
  const navigate = useNavigate()

  const [localSearchValue, setLocalSearchValue] = useState(searchValue)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [localDateRange, setLocalDateRange] = useState<DateRange | undefined>(selectedDate)

  useEffect(() => {
    if (selectedDate) {
      setLocalDateRange(selectedDate)
    } else {
      const today = new Date()
      setLocalDateRange({ from: today, to: today })
    }
  }, [selectedDate])

  const isRootUser = user?.email === 'root@live.com'

  const tabs = isRootUser
    ? [
      { id: 'raw', label: 'Timeline Bruta', path: 'raw' },
      { id: 'curated', label: 'Timeline Curada', path: 'curated' },
      { id: 'rejected', label: 'Conteúdos Rejeitados', path: 'rejected' }
    ]
    : [
      { id: 'curated', label: 'Timeline Curada', path: 'curated' }
    ]

  const handleTabChange = (path: string) => {
    navigate(path)
  }

  const handleSearchClick = () => {
    onSearch?.(localSearchValue)
  }

  const handleClearSearch = () => {
    setLocalSearchValue('')
    onSearch?.('')
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch?.(localSearchValue)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-gray-900">Timeline</h1>
          <p className="text-gray-600">Confira os post recentes</p>
        </div>
        <Button
          className="bg-gray-900 hover:bg-gray-800 text-white"
          onClick={() => setIsCreateModalOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Adicionar Conteúdo
        </Button>
      </div>
      <div className="flex flex-col">
        <hr className="border-gray-200 mb-4" />
        <div className="flex items-center justify-between">
          {tabs.length > 1 && (
            <div className="flex space-x-1 bg-white p-1 rounded-lg">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.path)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === tab.id
                    ? 'text-gray-900'
                    : 'text-gray-600 hover:text-gray-900'
                    }`}
                  style={{
                    backgroundColor: activeTab === tab.id ? '#F5F5F5' : 'transparent'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        <div className={`flex items-center gap-3 ${tabs.length > 1 ? '' : 'ml-auto'}`}>
          <div className="relative">
            <Input
              placeholder="Pesquisar"
              className="pl-10 pr-10 w-64"
              iconLeft={<Search className="h-4 w-4 stroke-muted-foreground" />}
              value={localSearchValue}
              onChange={(e) => setLocalSearchValue(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            {localSearchValue && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button
            className="bg-gray-900 hover:bg-gray-800 text-white px-6"
            onClick={handleSearchClick}
          >
            Buscar
          </Button>
        </div>
        </div>
        <hr className="border-gray-200 mt-4" />
      </div>
      <div className="flex items-center gap-4">
        <DatePicker
          value={localDateRange}
          onChange={(range) => {
            if (range && 'from' in range) {
              setLocalDateRange(range)
              onDateChange?.(range)
            } else if (range instanceof Date) {
              const dateRange = { from: range, to: range }
              setLocalDateRange(dateRange)
              onDateChange?.(dateRange)
            } else {
              setLocalDateRange(undefined)
              onDateChange?.(undefined)
            }
          }}
          placeholder="Hoje"
          className="w-auto"
          mode={DatePickerMode.Range}
        />
      </div>
      {isCreateModalOpen && (
        <CreatePostModal
          open={isCreateModalOpen}
          onOpenChange={setIsCreateModalOpen}
          onSuccess={() => {
            setIsCreateModalOpen(false)
          }}
        />
      )}
    </div>
  )
}
